import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { formatWon } from '../utils/number';

interface InvitationData {
 invitationCode: string;
 fromFamilyName: string;
 fromFamilyHash: string;
 toFamilyName: string;
 toFamilyHash: string;
 inviterName: string;
 inviterEmail: string;
 proposedSlots: number;
 proposedPrice: number;
 sharingType: 'dedicated' | 'shared';
 dedicatedSlots?: number[];
 sharedSlots?: number[];
 message?: string;
 status: string;
 counterSlots?: number;
 counterPrice?: number;
 counterMessage?: string;
 createdAt: string;
 expiresAt: string;
}

const MoFamilyInvitationPage: React.FC = () => {
 const [searchParams] = useSearchParams();
 const navigate = useNavigate();
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 const [invitation, setInvitation] = useState<InvitationData | null>(null);
 const [showCounterForm, setShowCounterForm] = useState(false);
 const [counterForm, setCounterForm] = useState({
 counterSlots: 0,
 counterPrice: 0,
 counterMessage: ''
 });

 const invitationCode = searchParams.get('code');
 const isReview = searchParams.get('review') === 'true';

 useEffect(() => {
 if (invitationCode) {
 loadInvitation();
 } else {
 setError('초대 코드가 없습니다.');
 setLoading(false);
 }
 }, [invitationCode]);

 const loadInvitation = async () => {
 try {
 const response = await fetch(`/mofamily/invitation/${invitationCode}`, {
 credentials: 'include'
 });
 
 if (!response.ok) {
 if (response.status === 401) {
 window.location.href = '/login?redirect=' + encodeURIComponent(window.location.href);
 return;
 }
 throw new Error('초대 정보를 불러올 수 없습니다.');
 }
 
 const data = await response.json();
 setInvitation(data.data);
 setLoading(false);
 } catch (err) {
 console.error('Error loading invitation:', err);
 setError(err instanceof Error ? err.message : '초대 정보를 불러오는데 실패했습니다.');
 setLoading(false);
 }
 };

 const acceptInvitation = async () => {
 try {
 const response = await fetch(`/mofamily/accept-family-invitation/${invitationCode}`, {
 method: 'POST',
 credentials: 'include'
 });
 
 if (!response.ok) throw new Error('초대 수락에 실패했습니다.');
 
 alert('초대를 수락했습니다! 잠시 후 모패밀리 페이지로 이동합니다.');
 setTimeout(() => navigate('/mofamily'), 2000);
 } catch (err) {
 console.error('Error accepting invitation:', err);
 alert(err instanceof Error ? err.message : '초대 수락에 실패했습니다.');
 }
 };

 const rejectInvitation = async () => {
 if (!confirm('정말로 이 초대를 거절하시겠습니까?')) return;
 
 try {
 const response = await fetch(`/mofamily/reject-family-invitation/${invitationCode}`, {
 method: 'POST',
 credentials: 'include'
 });
 
 if (!response.ok) throw new Error('초대 거절에 실패했습니다.');
 
 alert('초대를 거절했습니다. 잠시 후 모패밀리 페이지로 이동합니다.');
 setTimeout(() => navigate('/mofamily'), 2000);
 } catch (err) {
 console.error('Error rejecting invitation:', err);
 alert(err instanceof Error ? err.message : '초대 거절에 실패했습니다.');
 }
 };

 const submitCounterOffer = async (e: React.FormEvent) => {
 e.preventDefault();
 
 try {
 const response = await fetch(`/mofamily/counter-family-invitation/${invitationCode}`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify(counterForm)
 });
 
 if (!response.ok) throw new Error('역제안 전송에 실패했습니다.');
 
 alert('역제안을 전송했습니다! 잠시 후 모패밀리 페이지로 이동합니다.');
 setTimeout(() => navigate('/mofamily'), 2000);
 } catch (err) {
 console.error('Error sending counter offer:', err);
 alert(err instanceof Error ? err.message : '역제안 전송에 실패했습니다.');
 }
 };

 const acceptCounterOffer = async () => {
 try {
 const response = await fetch(`/mofamily/accept-counter-offer/${invitationCode}`, {
 method: 'POST',
 credentials: 'include'
 });
 
 if (!response.ok) throw new Error('역제안 수락에 실패했습니다.');
 
 alert('역제안을 수락했습니다! 잠시 후 모패밀리 페이지로 이동합니다.');
 setTimeout(() => navigate('/mofamily'), 2000);
 } catch (err) {
 console.error('Error accepting counter offer:', err);
 alert(err instanceof Error ? err.message : '역제안 수락에 실패했습니다.');
 }
 };

 if (loading) {
 return (
 <div className="min-h-screen flex items-center justify-center p-4">
 <div className="bg-white shadow-lg p-8 max-w-2xl w-full">
 <div className="text-center py-8">
 <div className="inline-block animate-spin h-10 w-10 rounded-full border-4 border-gray-200 border-t-blue-500"></div>
 <p className="text-gray-500 mt-4">초대 정보를 불러오는 중...</p>
 </div>
 </div>
 </div>
 );
 }

 if (error) {
 return (
 <div className="min-h-screen flex items-center justify-center p-4">
 <div className="bg-white shadow-lg p-8 max-w-2xl w-full">
 <div className="text-center">
 <div className="text-red-500 text-6xl mb-4">⚠️</div>
 <h2 className="text-2xl font-bold mb-4">오류</h2>
 <p className="text-gray-600 mb-6">{error}</p>
 <a href="/mofamily" className="inline-block px-6 py-3 bg-blue-500 text-white hover:bg-blue-600">
 모패밀리로 돌아가기
 </a>
 </div>
 </div>
 </div>
 );
 }

 if (!invitation) return null;

 const getStatusText = (status: string) => {
 const statusMap: { [key: string]: string } = {
 'pending': '대기중',
 'accepted': '수락됨',
 'rejected': '거절됨',
 'expired': '만료됨',
 'countered': '역제안됨'
 };
 return statusMap[status] || status;
 };

 // 이미 처리된 초대
 if (invitation.status !== 'pending' && !(isReview && invitation.status === 'countered')) {
 return (
 <div className="min-h-screen flex items-center justify-center p-4">
 <div className="bg-white shadow-lg p-8 max-w-2xl w-full">
 <div className="text-center">
 <h2 className="text-2xl font-bold mb-4">초대 상태: {getStatusText(invitation.status)}</h2>
 <p className="text-gray-600 mb-6">이 초대는 이미 처리되었습니다.</p>
 <a href="/mofamily" className="inline-block px-6 py-3 bg-blue-500 text-white hover:bg-blue-600">
 모패밀리로 돌아가기
 </a>
 </div>
 </div>
 </div>
 );
 }

 // 역제안 검토
 if (isReview && invitation.status === 'countered') {
 return (
 <div className="min-h-screen flex items-center justify-center p-4">
 <div className="bg-white shadow-lg p-8 max-w-2xl w-full">
 <h2 className="text-2xl font-bold mb-6 text-center">역제안 검토</h2>
 
 <div className="bg-gray-50 p-6 mb-6">
 <h3 className="font-semibold mb-3">원래 제안</h3>
 <div className="space-y-2">
 <p><span className="font-medium">슬롯 수:</span> {invitation.proposedSlots}개</p>
 <p><span className="font-medium">슬롯당 일일 가격:</span> {formatWon(invitation.proposedPrice)}</p>
 </div>
 </div>
 
 <div className="bg-yellow-50 p-6 mb-6">
 <h3 className="font-semibold mb-3">역제안</h3>
 <div className="space-y-2">
 <p><span className="font-medium">슬롯 수:</span> {invitation.counterSlots}개</p>
 <p><span className="font-medium">슬롯당 일일 가격:</span> {formatWon(invitation.counterPrice)}</p>
 </div>
 </div>
 
 {invitation.counterMessage && (
 <div className="bg-gray-50 p-6 mb-6">
 <h3 className="font-semibold mb-3">역제안 메시지</h3>
 <p className="text-gray-700">{invitation.counterMessage}</p>
 </div>
 )}
 
 <div className="flex gap-3 justify-center">
 <button 
 onClick={acceptCounterOffer}
 className="px-6 py-3 bg-green-500 text-white hover:bg-green-600"
 >
 역제안 수락
 </button>
 <button 
 onClick={rejectInvitation}
 className="px-6 py-3 bg-red-500 text-white hover:bg-red-600"
 >
 거절
 </button>
 </div>
 </div>
 </div>
 );
 }

 // 일반 초대
 return (
 <div className="min-h-screen flex items-center justify-center p-4">
 <div className="bg-white shadow-lg p-8 max-w-2xl w-full">
 <h2 className="text-2xl font-bold mb-6 text-center">모패밀리 협업 초대</h2>
 
 <div className="bg-gray-50 p-6 mb-6">
 <h3 className="font-semibold mb-3">초대 정보</h3>
 <div className="space-y-2">
 <p><span className="font-medium">초대 보낸 Family:</span> {invitation.fromFamilyName}</p>
 <p><span className="font-medium">초대 받은 Family:</span> {invitation.toFamilyName}</p>
 <p><span className="font-medium">초대자:</span> {invitation.inviterName}</p>
 </div>
 </div>
 
 <div className="bg-blue-50 p-6 mb-6">
 <h3 className="font-semibold mb-3">제안 내용</h3>
 <div className="space-y-2">
 <p><span className="font-medium">공유 슬롯 수:</span> {invitation.proposedSlots}개</p>
 <p><span className="font-medium">슬롯당 일일 가격:</span> {formatWon(invitation.proposedPrice)}</p>
 <p><span className="font-medium">위임 방식:</span> {invitation.sharingType === 'dedicated' ? '독점(Dedicated)' : '공유(Shared)'}</p>
 {invitation.dedicatedSlots && invitation.dedicatedSlots.length > 0 && (
 <p><span className="font-medium">독점 슬롯:</span> {invitation.dedicatedSlots.join(', ')}번</p>
 )}
 {invitation.sharedSlots && invitation.sharedSlots.length > 0 && (
 <p><span className="font-medium">공유 슬롯:</span> {invitation.sharedSlots.join(', ')}번</p>
 )}
 </div>
 </div>
 
 {invitation.message && (
 <div className="bg-gray-50 p-6 mb-6">
 <h3 className="font-semibold mb-3">메시지</h3>
 <p className="text-gray-700">{invitation.message}</p>
 </div>
 )}
 
 {showCounterForm ? (
 <form onSubmit={submitCounterOffer} className="space-y-4 mb-6">
 <h3 className="font-semibold">역제안 하기</h3>
 
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">
 공유 슬롯 수
 </label>
 <input 
 type="number" 
 min="1" 
 required
 value={counterForm.counterSlots}
 onChange={(e) => setCounterForm({...counterForm, counterSlots: parseInt(e.target.value) || 0})}
 className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
 />
 </div>
 
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">
 슬롯당 일일 가격 (원)
 </label>
 <input 
 type="number" 
 min="0" 
 step="1000" 
 required
 value={counterForm.counterPrice}
 onChange={(e) => setCounterForm({...counterForm, counterPrice: parseInt(e.target.value) || 0})}
 className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
 />
 </div>
 
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">
 메시지 (선택사항)
 </label>
 <textarea 
 rows={3}
 value={counterForm.counterMessage}
 onChange={(e) => setCounterForm({...counterForm, counterMessage: e.target.value})}
 className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
 />
 </div>
 
 <div className="flex gap-3 justify-center">
 <button 
 type="submit"
 className="px-6 py-3 bg-yellow-500 text-white hover:bg-yellow-600"
 >
 역제안 보내기
 </button>
 <button 
 type="button"
 onClick={() => setShowCounterForm(false)}
 className="px-6 py-3 bg-gray-500 text-white hover:bg-gray-600"
 >
 취소
 </button>
 </div>
 </form>
 ) : (
 <div className="flex gap-3 justify-center">
 <button 
 onClick={acceptInvitation}
 className="px-6 py-3 bg-green-500 text-white hover:bg-green-600"
 >
 수락
 </button>
 <button 
 onClick={() => setShowCounterForm(true)}
 className="px-6 py-3 bg-yellow-500 text-white hover:bg-yellow-600"
 >
 역제안
 </button>
 <button 
 onClick={rejectInvitation}
 className="px-6 py-3 bg-red-500 text-white hover:bg-red-600"
 >
 거절
 </button>
 </div>
 )}
 </div>
 </div>
 );
};

export default MoFamilyInvitationPage;